import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { fetchUsers } from '../../utils/userApi';

const API_URL = 'https://system.sales-management.cloud/api/activity_notifications.php';

const initialRuleForm = {
  id: null,
  name: '',
  actionKey: '',
  titleTemplate: 'アクション通知',
  messageTemplate: '{actor_name} が {action_key} を実行しました。{summary}',
  notifyUrl: '/notices',
  isActive: true,
  targetUserIds: [],
};

const statusOptions = [
  { value: '', label: '全て' },
  { value: 'success', label: '成功' },
  { value: 'failed', label: '失敗' },
  { value: 'warning', label: '警告' },
];

function ActivityNotificationManagement() {
  const [tab, setTab] = useState('logs');
  const [availableActionKeys, setAvailableActionKeys] = useState([]);

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logError, setLogError] = useState('');
  const [logActionKey, setLogActionKey] = useState('');
  const [logStatus, setLogStatus] = useState('');
  const [logLimit, setLogLimit] = useState(100);

  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [ruleError, setRuleError] = useState('');
  const [ruleSuccess, setRuleSuccess] = useState('');
  const [ruleForm, setRuleForm] = useState(initialRuleForm);
  const [savingRule, setSavingRule] = useState(false);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const actionKeyOptions = useMemo(() => {
    const set = new Set(availableActionKeys);
    if (ruleForm.actionKey) {
      set.add(ruleForm.actionKey);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [availableActionKeys, ruleForm.actionKey]);

  const getToken = () => {
    const token = Cookies.get('auth_token');
    if (!token) {
      throw new Error('認証情報が見つかりません');
    }
    return token;
  };

  const loadUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const result = await fetchUsers({
        status: 'active',
        limit: 300,
        sort_by: 'name',
        sort_order: 'asc',
      });
      if (result.status === 'success') {
        setUsers(result.data?.users || []);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error('Failed to fetch users for rules:', err);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      setLogError('');

      const token = getToken();
      const params = new URLSearchParams({
        scope: 'logs',
        limit: String(logLimit),
      });
      if (logActionKey) {
        params.set('action_key', logActionKey);
      }
      if (logStatus) {
        params.set('status', logStatus);
      }

      const res = await fetch(`${API_URL}?${params.toString()}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await res.json();
      if (result.status === 'success') {
        setLogs(result.data?.logs || []);
      } else {
        setLogs([]);
        setLogError(result.message || 'アクティビティログの取得に失敗しました');
      }
    } catch (err) {
      console.error('Failed to fetch activity logs:', err);
      setLogs([]);
      setLogError(err.message || 'アクティビティログの取得に失敗しました');
    } finally {
      setLogsLoading(false);
    }
  }, [logActionKey, logStatus, logLimit]);

  const loadRules = useCallback(async () => {
    try {
      setRulesLoading(true);
      setRuleError('');

      const token = getToken();
      const res = await fetch(`${API_URL}?scope=rules`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await res.json();
      if (result.status === 'success') {
        setRules(result.data?.rules || []);
      } else {
        setRules([]);
        setRuleError(result.message || '通知ルールの取得に失敗しました');
      }
    } catch (err) {
      console.error('Failed to fetch activity rules:', err);
      setRules([]);
      setRuleError(err.message || '通知ルールの取得に失敗しました');
    } finally {
      setRulesLoading(false);
    }
  }, []);

  const loadActionKeys = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}?scope=action_keys`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await res.json();
      if (result.status === 'success') {
        setAvailableActionKeys(result.data?.action_keys || []);
      }
    } catch (err) {
      console.error('Failed to fetch action keys:', err);
      setAvailableActionKeys([]);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadLogs();
    loadRules();
    loadActionKeys();
  }, [loadUsers, loadLogs, loadRules, loadActionKeys]);

  const handleRuleFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRuleForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleRuleTargetsChange = (e) => {
    const options = Array.from(e.target.selectedOptions);
    const values = options.map((option) => Number(option.value)).filter((id) => id > 0);
    setRuleForm((prev) => ({
      ...prev,
      targetUserIds: values,
    }));
  };

  const resetRuleForm = () => {
    setRuleForm(initialRuleForm);
    setRuleError('');
    setRuleSuccess('');
  };

  const startEditRule = (rule) => {
    setRuleForm({
      id: rule.id,
      name: rule.name || '',
      actionKey: rule.action_key || '',
      titleTemplate: rule.title_template || 'アクション通知',
      messageTemplate: rule.message_template || '',
      notifyUrl: rule.notify_url || '/notices',
      isActive: !!rule.is_active,
      targetUserIds: rule.target_user_ids || [],
    });
    setTab('rules');
    setRuleError('');
    setRuleSuccess('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveRule = async (e) => {
    e.preventDefault();
    setRuleError('');
    setRuleSuccess('');

    if (!ruleForm.name.trim()) {
      setRuleError('ルール名を入力してください');
      return;
    }
    if (!ruleForm.actionKey.trim()) {
      setRuleError('対象アクションキーを選択してください');
      return;
    }
    if (ruleForm.targetUserIds.length === 0) {
      setRuleError('通知先ユーザーを1人以上選択してください');
      return;
    }

    try {
      setSavingRule(true);
      const token = getToken();

      const payload = {
        name: ruleForm.name.trim(),
        action_key: ruleForm.actionKey.trim(),
        title_template: ruleForm.titleTemplate.trim() || 'アクション通知',
        message_template: ruleForm.messageTemplate.trim(),
        notify_url: ruleForm.notifyUrl.trim() || '/notices',
        is_active: !!ruleForm.isActive,
        target_user_ids: ruleForm.targetUserIds,
      };

      let method = 'POST';
      if (ruleForm.id) {
        method = 'PUT';
        payload.id = ruleForm.id;
      }

      const res = await fetch(API_URL, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (result.status === 'success') {
        setRuleSuccess(ruleForm.id ? '通知ルールを更新しました' : '通知ルールを作成しました');
        resetRuleForm();
        await Promise.all([loadRules(), loadLogs(), loadActionKeys()]);
      } else {
        setRuleError(result.message || '通知ルールの保存に失敗しました');
      }
    } catch (err) {
      console.error('Failed to save rule:', err);
      setRuleError(err.message || '通知ルールの保存に失敗しました');
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteRule = async (rule) => {
    if (!window.confirm(`「${rule.name}」を削除しますか？`)) {
      return;
    }

    try {
      setRuleError('');
      setRuleSuccess('');
      const token = getToken();
      const res = await fetch(`${API_URL}?id=${rule.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await res.json();
      if (result.status === 'success') {
        setRuleSuccess('通知ルールを削除しました');
        if (ruleForm.id === rule.id) {
          resetRuleForm();
        }
        await Promise.all([loadRules(), loadLogs(), loadActionKeys()]);
      } else {
        setRuleError(result.message || '通知ルールの削除に失敗しました');
      }
    } catch (err) {
      console.error('Failed to delete rule:', err);
      setRuleError(err.message || '通知ルールの削除に失敗しました');
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">アクション通知管理</h1>
        <p className="text-sm text-gray-600 mt-2">サイト全体の操作ログ確認と、アクション連動通知ルールを管理できます。</p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setTab('logs')}
          className={`px-4 py-2 rounded-md text-sm ${tab === 'logs' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          アクティビティログ
        </button>
        <button
          type="button"
          onClick={() => setTab('rules')}
          className={`px-4 py-2 rounded-md text-sm ${tab === 'rules' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          通知ルール
        </button>
      </div>

      {tab === 'logs' && (
        <div className="bg-white rounded-lg shadow p-4 md:p-6 space-y-4">
          {logError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{logError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">アクションキー</label>
              <select
                value={logActionKey}
                onChange={(e) => setLogActionKey(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">全て</option>
                {actionKeyOptions.map((key) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">結果</label>
              <select
                value={logStatus}
                onChange={(e) => setLogStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">表示件数</label>
              <select
                value={logLimit}
                onChange={(e) => setLogLimit(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value={50}>50件</option>
                <option value={100}>100件</option>
                <option value={200}>200件</option>
                <option value={500}>500件</option>
              </select>
            </div>

            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={loadLogs}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                再取得
              </button>
            </div>
          </div>

          {logsLoading ? (
            <p className="text-sm text-gray-500">読み込み中...</p>
          ) : logs.length === 0 ? (
            <p className="text-sm text-gray-500">ログがありません</p>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <div key={log.id} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <ResultBadge status={log.result_status} />
                    <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">{log.action_key}</span>
                    <span className="text-xs text-gray-500">{formatDateTime(log.created_at)}</span>
                  </div>

                  <p className="text-sm text-gray-900 font-semibold">{log.summary || '-'}</p>
                  {log.details && <p className="text-sm text-gray-700 mt-1">{log.details}</p>}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-xs text-gray-600">
                    <p>実行者: {formatUser(log.actor_user_name, log.actor_user_id)}</p>
                    <p>対象ユーザー: {formatUser(log.target_user_name, log.target_user_id)}</p>
                    <p>対象種別: {log.target_type || '-'}</p>
                    <p>対象ID: {log.target_id || '-'}</p>
                    <p>経路: {(log.request_method || '-') + ' ' + (log.request_path || '-')}</p>
                    <p>IP: {log.ip_address || '-'}</p>
                  </div>

                  {log.meta && (
                    <pre className="mt-2 text-[11px] text-gray-700 bg-white border border-gray-200 rounded p-2 overflow-x-auto">
                      {JSON.stringify(log.meta, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'rules' && (
        <div className="space-y-4">
          {ruleError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{ruleError}</p>
            </div>
          )}
          {ruleSuccess && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-700">{ruleSuccess}</p>
            </div>
          )}

          <form onSubmit={handleSaveRule} className="bg-white rounded-lg shadow p-4 md:p-6 space-y-4">
            <h2 className="text-lg font-bold text-gray-900">{ruleForm.id ? '通知ルール編集' : '通知ルール作成'}</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ルール名</label>
                <input
                  type="text"
                  name="name"
                  value={ruleForm.name}
                  onChange={handleRuleFieldChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="例: お知らせ作成時の管理者通知"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">対象アクションキー</label>
                <select
                  name="actionKey"
                  value={ruleForm.actionKey}
                  onChange={handleRuleFieldChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">アクションを選択してください</option>
                  {actionKeyOptions.map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">通知タイトルテンプレート</label>
                <input
                  type="text"
                  name="titleTemplate"
                  value={ruleForm.titleTemplate}
                  onChange={handleRuleFieldChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="例: アクション通知"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">遷移先URL</label>
                <input
                  type="text"
                  name="notifyUrl"
                  value={ruleForm.notifyUrl}
                  onChange={handleRuleFieldChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="/notices"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">通知本文テンプレート</label>
              <textarea
                name="messageTemplate"
                value={ruleForm.messageTemplate}
                onChange={handleRuleFieldChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="{actor_name} が {action_key} を実行しました。{summary}"
              />
              <p className="text-xs text-gray-500 mt-1">利用可能: {'{actor_name}'} {'{action_key}'} {'{summary}'} {'{result_status}'} {'{target_type}'} {'{target_id}'} {'{log_id}'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">通知先ユーザー（複数選択可）</label>
              <select
                multiple
                value={ruleForm.targetUserIds.map(String)}
                onChange={handleRuleTargetsChange}
                disabled={usersLoading}
                className="w-full h-44 px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Mac: command / Windows: Ctrl で複数選択できます。</p>
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                name="isActive"
                checked={ruleForm.isActive}
                onChange={handleRuleFieldChange}
              />
              このルールを有効にする
            </label>

            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={savingRule}
                className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-60"
              >
                {savingRule ? '保存中...' : ruleForm.id ? '更新する' : '作成する'}
              </button>
              {ruleForm.id && (
                <button
                  type="button"
                  onClick={resetRuleForm}
                  className="px-5 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                >
                  編集をキャンセル
                </button>
              )}
            </div>
          </form>

          <div className="bg-white rounded-lg shadow p-4 md:p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">登録済み通知ルール</h2>

            {rulesLoading ? (
              <p className="text-sm text-gray-500">読み込み中...</p>
            ) : rules.length === 0 ? (
              <p className="text-sm text-gray-500">ルールがありません</p>
            ) : (
              <div className="space-y-3">
                {rules.map((rule) => (
                  <div key={rule.id} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">{rule.action_key}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${rule.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                        {rule.is_active ? '有効' : '無効'}
                      </span>
                      <span className="text-xs text-gray-500">更新: {formatDateTime(rule.updated_at)}</span>
                    </div>

                    <p className="text-sm font-semibold text-gray-900">{rule.name}</p>
                    <p className="text-xs text-gray-600 mt-1">URL: {rule.notify_url || '-'}</p>
                    <p className="text-xs text-gray-600 mt-1">通知先: {(rule.target_users || []).map((u) => u.name).join(', ') || '-'}</p>

                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => startEditRule(rule)}
                        className="px-3 py-1.5 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        編集
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteRule(rule)}
                        className="px-3 py-1.5 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                      >
                        削除
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ResultBadge({ status }) {
  if (status === 'success') {
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700">成功</span>;
  }
  if (status === 'warning') {
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">警告</span>;
  }
  return <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-700">失敗</span>;
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }
  return new Date(value).toLocaleString('ja-JP');
}

function formatUser(name, id) {
  if (name) {
    return `${name} (ID: ${id ?? '-'})`;
  }
  return id ? `ID: ${id}` : '-';
}

export default ActivityNotificationManagement;
